if (!customElements.get('cinematic-fashion-banner')) {
  class CinematicFashionBanner extends HTMLElement {
    constructor() {
      super();
      this._navObserver = null;
      this._onResize = null;
      this._sliderActive = false;
      this._slideTimer = null;
      this._slideIndex = 0;
      this._slideTrack = null;
      this._cloneEl = null;
      this._mobileMql = null;
      this._onMobileChange = null;
      this._videoSlots = null;
      this._videoTargets = [];
      this._videoAbort = null;
      this._videoObserver = null;
      this._videoMql = null;
      this._reducedMql = null;
      this._onScreen = false;
      this._settleHandler = null;
      this._turnInterval = 0;
      this._turnIndex = 0;
      this._turnRemaining = 0;
      this._turnStartedAt = 0;
      this._turnTimer = null;
      this._turnPlayed = false;
      this._desktopMode = 'all';
      this._swipe = false;
      this._swipeAbort = null;
      this._swipeCompanion = -1;
      this._timedByVideo = false;
      this._slideInterval = 0;
      this._slideTimeout = null;
      this._slidePlayed = false;
    }

    connectedCallback() {
      this.scrollContainer = this.querySelector('.cinematic-banner__menu-scroll');
      this.menuWrapper = this.querySelector('.cinematic-banner__menu-wrapper');

      if (this.scrollContainer) {
        this.setupDragScroll();
      }

      this._slotOrder = Array.from(
        this.querySelectorAll('.cinematic-banner__images > .cinematic-banner__image-wrapper')
      );
      this.setupNavigationObserver();
      this.setupMobileSlider();
      this.setupVideoPlayback();
    }

    disconnectedCallback() {
      this.teardownVideoPlayback();
      if (this._navObserver) {
        this._navObserver.disconnect();
        this._navObserver = null;
      }
      if (this._onResize) {
        window.removeEventListener('resize', this._onResize);
        this._onResize = null;
      }
      if (this._mobileMql && this._onMobileChange) {
        this._mobileMql.removeEventListener('change', this._onMobileChange);
      }
      this.stopMobileSlider();
    }

    setupNavigationObserver() {
      const checkNavigation = () => {
        if (!this.menuWrapper) return;

        if (window.innerWidth <= 749) {
          this.menuWrapper.classList.remove('is-hidden');
          return;
        }

        const headerDetailsOpen = document.querySelectorAll('header details[open]').length > 0;
        const menuDrawerOpen = !!document.querySelector('menu-drawer[open]');
        const hasOpenDropdown = headerDetailsOpen || menuDrawerOpen;

        this.menuWrapper.classList.toggle('is-hidden', hasOpenDropdown);
      };

      this._navObserver = new MutationObserver(checkNavigation);

      document.querySelectorAll('header details').forEach((details) => {
        this._navObserver.observe(details, { attributes: true, attributeFilter: ['open'] });
      });

      const menuDrawer = document.querySelector('menu-drawer');
      if (menuDrawer) {
        this._navObserver.observe(menuDrawer, { attributes: true, attributeFilter: ['open'] });
      }

      this._onResize = checkNavigation;
      window.addEventListener('resize', this._onResize);
      checkNavigation();
    }

    setupDragScroll() {
      let isDown = false;
      let startX = 0;
      let scrollLeft = 0;
      const el = this.scrollContainer;

      el.addEventListener('mousedown', (e) => {
        isDown = true;
        el.style.cursor = 'grabbing';
        startX = e.pageX - el.offsetLeft;
        scrollLeft = el.scrollLeft;
      });

      el.addEventListener('mouseleave', () => {
        isDown = false;
        el.style.cursor = 'grab';
      });

      el.addEventListener('mouseup', () => {
        isDown = false;
        el.style.cursor = 'grab';
      });

      el.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - el.offsetLeft;
        const walk = (x - startX) * 2;
        el.scrollLeft = scrollLeft - walk;
      });
    }

    setupMobileSlider() {
      const slideCount = parseInt(this.dataset.slideCount, 10) || 0;
      if (slideCount < 2) return;

      this._mobileMql = window.matchMedia('(max-width: 749px)');
      this._onMobileChange = () => {
        if (this._mobileMql.matches) this.startMobileSlider();
        else this.stopMobileSlider();
      };
      this._mobileMql.addEventListener('change', this._onMobileChange);
      this._onMobileChange();
    }

    startMobileSlider() {
      if (this._sliderActive) return;
      const track = this.querySelector('.cinematic-banner__images');
      if (!track) return;
      const slideCount = parseInt(this.dataset.slideCount, 10) || 0;
      if (slideCount < 2) return;

      this._hiddenBlanks = Array.from(
        track.querySelectorAll('.cinematic-banner__image-wrapper[data-cb-blank]')
      );
      this._hiddenBlanks.forEach((el) => {
        el.dataset.cbPrevDisplay = el.style.display || '';
        el.style.display = 'none';
      });

      this.applyMobileOrder(track);

      if (this.dataset.mobileAdvance === 'manual') {
        this.startSwipeSlider(track, slideCount);
        return;
      }

      const firstWrapper = track.querySelector(
        '.cinematic-banner__image-wrapper:not([data-cb-blank])'
      );
      if (!firstWrapper) return;
      if (firstWrapper.hasAttribute('data-cb-video')) {
        // The loop clone of a video slot is its still alone, never a second <video>.
        this._cloneEl = firstWrapper.cloneNode(false);
        this._cloneEl.removeAttribute('data-cb-video');
        this._cloneEl.removeAttribute('data-cb-playing');
        const still = firstWrapper.querySelector('.cinematic-banner__video-still');
        if (still) this._cloneEl.appendChild(still.cloneNode(true));
      } else {
        this._cloneEl = firstWrapper.cloneNode(true);
      }
      this._cloneEl.setAttribute('aria-hidden', 'true');
      this._cloneEl.dataset.cbClone = 'true';
      const clonedImg = this._cloneEl.querySelector('img');
      if (clonedImg) clonedImg.removeAttribute('fetchpriority');
      track.appendChild(this._cloneEl);

      this._slideTrack = track;
      this._slideCount = slideCount;
      this._slideIndex = 0;
      this._sliderActive = true;
      this.syncVideos();

      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduced) return;

      const interval = parseInt(this.dataset.slideInterval, 10) || 4000;
      if (track.querySelector('.cinematic-banner__image-wrapper[data-cb-video]:not([data-cb-clone]) video')) {
        this._timedByVideo = true;
        this._slideInterval = interval;
        this.scheduleSlide();
        return;
      }
      this._slideTimer = setInterval(() => this.advanceSlide(), interval);
    }

    /*
      Auto advance on a banner with a video slot. Each slide gets the slide interval
      from the moment the track starts moving to it. An image slide advances when
      that runs out, and so does a video slide whose video has not started playing
      by then (blocked, failed, no sources, play refused), so the slideshow never
      waits on a video that cannot play. A video that starts playing within the
      interval holds its slide until it ends (see onVideoPlaying and the ended
      handler), watched by watchProgress so a video that stalls mid-play advances
      too. The loop clone is a still, so it is timed like an image.
    */
    scheduleSlide() {
      clearTimeout(this._slideTimeout);
      this._slidePlayed = false;
      this._slideTimeout = setTimeout(() => this.nextSlide(), this._slideInterval);
    }

    nextSlide() {
      clearTimeout(this._slideTimeout);
      this._slideTimeout = null;
      this._slidePlayed = false;
      this.advanceSlide();
      this.scheduleSlide();
    }

    /*
      Manual advance. The track becomes a native horizontal scroller (.cb-swipe in
      section-cinematic-fashion-banner.css) that snaps to whole slides and stops at
      the first and last, so there is no loop clone and no timer. Scrolling keeps
      the slide index current for video playback.
    */
    startSwipeSlider(track, slideCount) {
      this._slideTrack = track;
      this._slideCount = slideCount;
      this._slideIndex = 0;
      this._swipeCompanion = -1;
      this._swipe = true;
      this._sliderActive = true;
      track.classList.add('cb-swipe');
      track.scrollLeft = 0;
      this._swipeAbort = new AbortController();
      track.addEventListener('scroll', () => this.onSwipeScroll(), {
        passive: true,
        signal: this._swipeAbort.signal,
      });
      this.syncVideos();
    }

    /*
      The visible slide is the one nearest its resting place. Mid-swipe the other
      slide partly on screen is its companion: an arriving video starts as its slide
      comes into view and a leaving one plays until it is fully off screen, as in
      auto advance. At rest (within 1px of a snap position) there is no companion.
    */
    onSwipeScroll() {
      const track = this._slideTrack;
      const width = track.clientWidth;
      if (!width) return;
      const pos = track.scrollLeft / width;
      const index = Math.min(this._slideCount - 1, Math.max(0, Math.round(pos)));
      let companion = -1;
      if (Math.abs(pos - index) * width >= 1) {
        companion = pos > index ? index + 1 : index - 1;
        if (companion >= this._slideCount) companion = -1;
      }
      if (index === this._slideIndex && companion === this._swipeCompanion) return;
      this._slideIndex = index;
      this._swipeCompanion = companion;
      this.syncVideos();
    }

    advanceSlide() {
      if (!this._slideTrack) return;
      const outgoing = this._videoSlots ? this.visibleSlide() : null;
      this._slideIndex += 1;
      this._slideTrack.classList.remove('cb-no-transition');
      this._slideTrack.style.transform = `translateX(-${this._slideIndex * 100}%)`;

      if (this._slideIndex >= this._slideCount) {
        const onEnd = () => {
          this._slideTrack.removeEventListener('transitionend', onEnd);
          this._slideTrack.classList.add('cb-no-transition');
          this._slideIndex = 0;
          this._slideTrack.style.transform = 'translateX(0)';
          void this._slideTrack.offsetWidth;
          this._slideTrack.classList.remove('cb-no-transition');
        };
        this._slideTrack.addEventListener('transitionend', onEnd);
      }

      if (this._videoSlots) this.onVideoSlideAdvance(outgoing);
    }

    stopMobileSlider() {
      if (this._slideTimer) {
        clearInterval(this._slideTimer);
        this._slideTimer = null;
      }
      if (this._slideTimeout) {
        clearTimeout(this._slideTimeout);
        this._slideTimeout = null;
      }
      this._timedByVideo = false;
      this._slidePlayed = false;
      if (this._swipeAbort) {
        this._swipeAbort.abort();
        this._swipeAbort = null;
      }
      if (this._swipe && this._slideTrack) {
        this._slideTrack.scrollLeft = 0;
        this._slideTrack.classList.remove('cb-swipe');
      }
      this._swipe = false;
      this._swipeCompanion = -1;
      if (this._cloneEl) {
        this._cloneEl.remove();
        this._cloneEl = null;
      }
      this.restoreSlotOrder();
      if (this._hiddenBlanks) {
        this._hiddenBlanks.forEach((el) => {
          el.style.display = el.dataset.cbPrevDisplay || '';
          delete el.dataset.cbPrevDisplay;
        });
        this._hiddenBlanks = null;
      }
      if (this._slideTrack) {
        if (this._settleHandler) {
          this._slideTrack.removeEventListener('transitionend', this._settleHandler);
          this._settleHandler = null;
        }
        this._slideTrack.classList.add('cb-no-transition');
        this._slideTrack.style.transform = '';
        void this._slideTrack.offsetWidth;
        this._slideTrack.classList.remove('cb-no-transition');
      }
      this._slideTrack = null;
      this._slideIndex = 0;
      this._sliderActive = false;
      this.syncVideos();
    }

    /*
      Mobile first slide (data-mobile-first, 2 or 3): the mobile slideshow starts at
      that slot and continues in slot order, wrapping, so 2 gives 2-3-1 and 3 gives
      3-1-2. The slots before it move to the end of the track while the slideshow
      runs; restoreSlotOrder puts them back for desktop. A blank chosen slot leaves
      the order alone.
    */
    applyMobileOrder(track) {
      const first = parseInt(this.dataset.mobileFirst, 10);
      if (!(first > 1)) return;
      const lead = this._slotOrder[first - 1];
      if (!lead || lead.hasAttribute('data-cb-blank')) return;
      this._slotOrder.slice(0, first - 1).forEach((slot) => track.appendChild(slot));
    }

    restoreSlotOrder() {
      const track = this.querySelector('.cinematic-banner__images');
      if (!track || !this._slotOrder) return;
      if (this._slotOrder.every((slot, i) => track.children[i] === slot)) return;
      this._slotOrder.forEach((slot) => track.appendChild(slot));
    }

    /*
      Video slots. Everything below runs only when the section rendered at least one
      slot with a video; an image-only banner never reaches it.

      Videos play only while the banner is on screen and the visitor has not asked
      for reduced motion. A slot's still covers its video whenever the video is not
      playing, so a video that cannot play shows its still.

      Desktop follows Desktop video playback (data-desktop-playback):
      - All at once (no attribute): every video slot plays, each looping.
      - Round robin, timed ("timed"): the video slots take turns. A turn lasts the
        slide interval. Its clock starts when the turn begins and runs whether or
        not the video plays, so a video that never starts shows its still and hands
        off on time. When the turn ends the next video slot plays, wrapping to the
        first. A video resumes where it stopped on its next turn.
      - Round robin, full length ("full"): the video slots take turns, and a turn
        lasts until its video ends. A video that has not started playing when the
        slide interval runs out is skipped, and one that stops advancing for a whole
        slide interval mid-play is skipped too.
      In both round robins, while the banner is off screen or reduced motion is on,
      the turn clock pauses and keeps its remaining time, and the same video
      finishes its turn on return. With a single video slot there is no handoff and
      it plays continuously.

      Mobile: the video in the visible slide plays. Auto advance times each video
      slide by its video (see scheduleSlide); the outgoing slide keeps playing until
      the slide transition ends, and while the loop clone is showing nothing plays,
      because the clone is a still. Manual advance also plays the other slide partly
      on screen mid-swipe (see onSwipeScroll).

      Videos have no loop attribute: one that ends while it should keep playing is
      rewound and played again; one playing through to its end hands on instead.
      Pausing keeps currentTime, so a video resumes where it stopped.
    */
    setupVideoPlayback() {
      // Slot order, not DOM order: on mobile the DOM may be rotated (applyMobileOrder),
      // and the desktop round robin takes turns in slot order.
      const slots = this._slotOrder.filter(
        (slot) => slot.hasAttribute('data-cb-video') && slot.querySelector('video')
      );
      if (!slots.length) return;

      this._videoSlots = slots;
      this._videoAbort = new AbortController();
      const { signal } = this._videoAbort;

      const mode = this.dataset.desktopPlayback;
      this._desktopMode = mode === 'timed' || mode === 'full' ? mode : 'all';
      this._turnInterval = parseInt(this.dataset.slideInterval, 10) || 4000;
      this._turnIndex = 0;
      this._turnRemaining = this._turnInterval;
      this._turnPlayed = false;

      slots.forEach((slot) => {
        const video = slot.querySelector('video');
        video.muted = true;

        video.addEventListener(
          'play',
          () => {
            // Only targets play. The browser can resume a video by itself, such as
            // one it paused while the tab was hidden, so anything else is stopped.
            if (!this.isVideoTarget(slot)) video.pause();
          },
          { signal }
        );

        video.addEventListener(
          'playing',
          () => {
            if (video.paused) return;
            this.onVideoPlaying(slot);
            this.revealOnFrame(slot, video);
          },
          { signal }
        );

        video.addEventListener(
          'pause',
          () => {
            // Reaching the end fires pause, then ended. The ended handler rewinds a
            // video that should keep playing, or hands on from one that played
            // through, so its still stays hidden.
            if (video.ended && this.isVideoTarget(slot)) return;
            delete slot.dataset.cbPlaying;
          },
          { signal }
        );

        video.addEventListener(
          'ended',
          () => {
            if (!this.isVideoTarget(slot)) {
              delete slot.dataset.cbPlaying;
              return;
            }
            if (this.playsThrough(slot)) {
              if (this.isMobile()) this.nextSlide();
              else this.endTurn();
              return;
            }
            video.currentTime = 0;
            this.playSlot(slot);
          },
          { signal }
        );
      });

      const sync = () => this.syncVideos();
      this._videoMql = window.matchMedia('(max-width: 749px)');
      this._reducedMql = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._videoMql.addEventListener('change', sync, { signal });
      this._reducedMql.addEventListener('change', sync, { signal });

      this._onScreen = false;
      this._videoObserver = new IntersectionObserver((entries) => {
        this._onScreen = entries[entries.length - 1].isIntersecting;
        this.syncVideos();
      });
      this._videoObserver.observe(this);
    }

    teardownVideoPlayback() {
      if (!this._videoSlots) return;
      const slots = this._videoSlots;
      this._videoSlots = null;
      this._videoTargets = [];
      this._turnPlayed = false;
      this._videoAbort.abort();
      this._videoAbort = null;
      this._videoObserver.disconnect();
      this._videoObserver = null;
      if (this._turnTimer) {
        clearTimeout(this._turnTimer);
        this._turnTimer = null;
      }
      if (this._settleHandler && this._slideTrack) {
        this._slideTrack.removeEventListener('transitionend', this._settleHandler);
      }
      this._settleHandler = null;
      slots.forEach((slot) => this.pauseSlot(slot));
    }

    isMobile() {
      return this._videoMql.matches;
    }

    // The slides of the running mobile slideshow, in order, without the loop clone.
    sliderSlides() {
      return this._slideTrack.querySelectorAll(
        '.cinematic-banner__image-wrapper:not([data-cb-blank]):not([data-cb-clone])'
      );
    }

    // The slide the mobile track is showing. Null while the loop clone is showing.
    visibleSlide() {
      const track = this.querySelector('.cinematic-banner__images');
      if (!track) return null;
      if (!this._sliderActive) {
        return track.querySelector('.cinematic-banner__image-wrapper');
      }
      if (this._slideIndex >= this._slideCount) return null;
      return this.sliderSlides()[this._slideIndex] || null;
    }

    // Manual advance: the other slide partly on screen mid-swipe, or null.
    swipeCompanion() {
      if (!this._swipe || this._swipeCompanion < 0) return null;
      return this.sliderSlides()[this._swipeCompanion] || null;
    }

    // The slots that should be playing now.
    videoTargets() {
      if (!this._onScreen || this._reducedMql.matches) return [];
      if (this.isMobile()) {
        return [this.visibleSlide(), this.swipeCompanion()].filter((slide) =>
          this._videoSlots.includes(slide)
        );
      }
      if (this._desktopMode === 'all') return this._videoSlots.slice();
      return [this._videoSlots[this._turnIndex]];
    }

    isVideoTarget(slot) {
      return this._videoTargets.includes(slot);
    }

    // `keep` is left as it is: on mobile, the outgoing slide while it slides away.
    syncVideos(keep = null) {
      if (!this._videoSlots) return;
      const targets = this.videoTargets();
      this._videoTargets = targets;

      this._videoSlots.forEach((slot) => {
        if (!targets.includes(slot) && slot !== keep) this.pauseSlot(slot);
      });

      // The desktop turn clock follows the target, not playback: it runs whenever
      // there is a target, whether or not its video manages to play.
      if (!targets.length || this.isMobile()) this.pauseTurnClock();
      else this.startTurnClock();
      if (this.isMobile()) this.syncSlideWatch();
      targets.forEach((slot) => this.playSlot(slot));
    }

    /*
      A video that plays through to its end: the visible slide under auto advance
      with a video clock, or the turn's video in round robin, full length (two or
      more video slots). Its end hands on to the next slide or turn.
    */
    playsThrough(slot) {
      if (this.isMobile()) {
        return this._timedByVideo && this._slidePlayed && slot === this.visibleSlide();
      }
      return this._turnPlayed && slot === this._videoSlots[this._turnIndex];
    }

    /*
      The still stays over its video until the video has put a frame on screen.
      'playing' fires once playback has started, which with an HLS stream (iOS
      Safari, Shopify's m3u8 source) can be before the first frame is composited;
      hiding the still on 'playing' then shows an empty video box for a beat.
      requestVideoFrameCallback runs when a frame has been presented, so the still
      goes only once there is a picture under it. A video paused before its first
      frame keeps its still; the callback fires on the next presented frame, when
      it is playing again. Browsers without requestVideoFrameCallback hide the
      still on 'playing' as before.
    */
    revealOnFrame(slot, video) {
      if (typeof video.requestVideoFrameCallback !== 'function') {
        slot.dataset.cbPlaying = 'true';
        return;
      }
      video.requestVideoFrameCallback(() => {
        if (video.paused) return;
        slot.dataset.cbPlaying = 'true';
      });
    }

    // A video started playing. One that plays through now holds its slide or turn
    // until it ends; its interval clock gives way to the stall watch.
    onVideoPlaying(slot) {
      if (this.isMobile()) {
        if (!this._timedByVideo || this._slidePlayed || slot !== this.visibleSlide()) return;
        this._slidePlayed = true;
        clearTimeout(this._slideTimeout);
        this._slideTimeout = null;
        this.syncSlideWatch();
        return;
      }
      if (this._desktopMode !== 'full' || this._turnPlayed || this._videoSlots.length < 2) return;
      if (slot !== this._videoSlots[this._turnIndex]) return;
      this._turnPlayed = true;
      this.pauseTurnClock();
      this.startTurnClock();
    }

    // Auto advance: the stall watch on a slide held by its video runs only while
    // that video should be playing, so a video paused off screen holds its slide
    // and resumes where it stopped.
    syncSlideWatch() {
      if (!this._timedByVideo || !this._slidePlayed) return;
      const slide = this.visibleSlide();
      if (this.isVideoTarget(slide)) {
        if (!this._slideTimeout) this.watchProgress(slide, '_slideTimeout', () => this.nextSlide());
      } else if (this._slideTimeout) {
        clearTimeout(this._slideTimeout);
        this._slideTimeout = null;
      }
    }

    // Stores a timer in this[field] that calls onStall once the slot's video has
    // gone a whole slide interval without advancing, re-arming while it advances.
    watchProgress(slot, field, onStall) {
      const video = slot.querySelector('video');
      const mark = video.currentTime;
      this[field] = setTimeout(() => {
        this[field] = null;
        if (video.currentTime !== mark) this.watchProgress(slot, field, onStall);
        else onStall();
      }, this._turnInterval);
    }

    onVideoSlideAdvance(outgoing) {
      const track = this._slideTrack;
      if (this._settleHandler) track.removeEventListener('transitionend', this._settleHandler);
      this._settleHandler = (event) => {
        if (event.target !== track || event.propertyName !== 'transform') return;
        track.removeEventListener('transitionend', this._settleHandler);
        this._settleHandler = null;
        this.syncVideos();
      };
      track.addEventListener('transitionend', this._settleHandler);
      this.syncVideos(outgoing);
    }

    playSlot(slot) {
      const video = slot.querySelector('video');
      if (!video.paused) return;
      const attempt = video.play();
      if (attempt !== undefined) attempt.catch(() => {});
    }

    pauseSlot(slot) {
      const video = slot.querySelector('video');
      if (video && !video.paused) video.pause();
      delete slot.dataset.cbPlaying;
    }

    // Round robin only. Until the turn's video plays through (full length), the
    // clock is the turn's interval; after that it is the stall watch.
    startTurnClock() {
      if (this._turnTimer || this._videoSlots.length < 2 || this.isMobile()) return;
      if (this._desktopMode === 'all') return;
      const slot = this._videoSlots[this._turnIndex];
      if (!this.isVideoTarget(slot)) return;
      if (this._turnPlayed) {
        this.watchProgress(slot, '_turnTimer', () => this.endTurn());
        return;
      }
      this._turnStartedAt = performance.now();
      this._turnTimer = setTimeout(() => this.endTurn(), this._turnRemaining);
    }

    pauseTurnClock() {
      if (!this._turnTimer) return;
      clearTimeout(this._turnTimer);
      this._turnTimer = null;
      if (this._turnPlayed) return;
      const elapsed = performance.now() - this._turnStartedAt;
      this._turnRemaining = Math.max(0, this._turnRemaining - elapsed);
    }

    endTurn() {
      clearTimeout(this._turnTimer);
      this._turnTimer = null;
      this._turnPlayed = false;
      this._turnIndex = (this._turnIndex + 1) % this._videoSlots.length;
      this._turnRemaining = this._turnInterval;
      this.syncVideos();
    }
  }

  customElements.define('cinematic-fashion-banner', CinematicFashionBanner);
}
